import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Skeleton } from "../../components/ui/skeleton";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "../../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import StickyHeading from "../../components/common/StickyHeading";
import { Sparkles, MapPin, Plus, Pencil, Archive, RotateCcw, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { isPendingMasterChange, sentForApprovalMessage } from "../../lib/masterChange";
import MasterChangeDestination from "../../components/common/MasterChangeDestination";

const KIND_LABEL = { district: "District", patna_zone: "Patna zone" };

/**
 * Territory Master (Product Master pattern): the 41 seeded units plus anything
 * added later. Archive, not delete — units are referenced by user assignments
 * and by history, and the backend refuses to archive one still assigned
 * (409 naming the holders), which this page surfaces verbatim.
 */
export default function TerritoryMasterPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null); // unit being renamed
  const [form, setForm] = useState({ name: "", kind: "district" });
  const [saving, setSaving] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [archiving, setArchiving] = useState(false);

  async function load() {
    setError("");
    try {
      const { data } = await api.get("/settings/territories");
      setItems(data.items || []);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function openAdd() {
    setEditing(null);
    setForm({ name: "", kind: "district" });
    setDialogOpen(true);
  }

  function openRename(unit) {
    setEditing(unit);
    setForm({ name: unit.name, kind: unit.kind });
    setDialogOpen(true);
  }

  async function save() {
    if (!form.name.trim()) return toast.error("Unit name is required");
    setSaving(true);
    try {
      if (editing) {
        const { data } = await api.put(`/settings/territories/${editing.id}`, { name: form.name.trim() });
        if (isPendingMasterChange(data)) {
          toast.success(sentForApprovalMessage(data));
        } else if (data.holders_updated) {
          toast.success(`Renamed — updated on ${data.holders_updated} user${data.holders_updated === 1 ? "" : "s"}`);
        } else {
          toast.success("Renamed");
        }
      } else {
        const { data } = await api.post("/settings/territories", { name: form.name.trim(), kind: form.kind });
        toast.success(isPendingMasterChange(data) ? sentForApprovalMessage(data) : "Unit added");
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  }

  async function confirmArchive() {
    if (!archiveTarget) return;
    setArchiving(true);
    try {
      const { data } = await api.post(`/settings/territories/${archiveTarget.id}/archive`);
      toast.success(isPendingMasterChange(data) ? sentForApprovalMessage(data) : "Unit archived");
      setArchiveTarget(null);
      load();
    } catch (e) {
      // The 409 names the users still holding the unit — show it as-is.
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setArchiveTarget(null);
    } finally {
      setArchiving(false);
    }
  }

  async function restore(unit) {
    try {
      const { data } = await api.post(`/settings/territories/${unit.id}/restore`);
      toast.success(isPendingMasterChange(data) ? sentForApprovalMessage(data) : "Unit restored");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  const zones = items.filter((u) => u.kind === "patna_zone");
  const districts = items.filter((u) => u.kind !== "patna_zone");
  const activeCount = items.filter((u) => !u.archived).length;

  return (
    <div className="space-y-6" data-testid="territory-master-page">
      <StickyHeading
        icon={Sparkles}
        label="Territory Master"
        title="Territories"
        subtitle={`${activeCount} assignable unit${activeCount === 1 ? "" : "s"} — Bihar districts plus the four Patna zones. Officers and managers can hold several.`}
        testId="territory-master-heading"
        actions={
          <Button
            onClick={openAdd}
            className="h-11 bg-callisto-navy hover:bg-callisto-navy/90 text-white shrink-0"
            data-testid="territory-add"
          >
            <Plus size={16} className="mr-1.5" /> Add unit
          </Button>
        }
      />

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 text-red-800 px-3 py-2.5 text-sm">{error}</div>
      )}

      {loading ? (
        <Skeleton className="h-64 rounded-xl" />
      ) : (
        <>
          <UnitSection title="Patna zones" units={zones} onRename={openRename} onArchive={setArchiveTarget} onRestore={restore} testId="territory-zones" />
          <UnitSection title="Districts" units={districts} onRename={openRename} onArchive={setArchiveTarget} onRestore={restore} testId="territory-districts" />
        </>
      )}

      <Dialog open={dialogOpen} onOpenChange={(o) => !o && setDialogOpen(false)}>
        <DialogContent className="sm:max-w-md" data-testid="territory-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? `Rename ${editing.name}` : "Add unit"}</DialogTitle>
            <DialogDescription>
              {editing
                ? "The new name replaces the old one on every user currently holding this unit. History keeps the old name."
                : "A new assignable unit. The seeded Bihar set should rarely need additions."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-callisto-navy">Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="h-11 mt-1.5 focus-visible:ring-callisto-gold focus-visible:ring-offset-0"
                data-testid="territory-form-name"
              />
            </div>
            {!editing && (
              <div>
                <Label className="text-callisto-navy">Kind</Label>
                <Select value={form.kind} onValueChange={(kind) => setForm((f) => ({ ...f, kind }))}>
                  <SelectTrigger className="h-11 mt-1.5 focus:ring-callisto-gold" data-testid="territory-form-kind">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="district">District</SelectItem>
                    <SelectItem value="patna_zone">Patna zone</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
            <Button
              onClick={save}
              disabled={saving}
              className="bg-callisto-navy hover:bg-callisto-navy/90 text-white"
              data-testid="territory-form-save"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Save size={14} className="mr-1.5" /> Save</>)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!archiveTarget} onOpenChange={(o) => !o && setArchiveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Archive {archiveTarget?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              <MasterChangeDestination testId="territory-archive-destination" />
              <span className="block mt-2">
                Nothing changes yet. Once approved the unit disappears from every
                picker but stays in past records and history. Archiving is refused
                while anybody still holds the unit.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={archiving}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmArchive(); }}
              disabled={archiving}
              className="bg-orange-600 hover:bg-orange-700"
              data-testid="territory-archive-confirm"
            >
              Archive
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function UnitSection({ title, units, onRename, onArchive, onRestore, testId }) {
  if (units.length === 0) return null;
  return (
    <section data-testid={testId}>
      <h2 className="text-[11px] font-bold uppercase tracking-[0.2em] text-callisto-gold mb-2">
        {title} · {units.length}
      </h2>
      <div className="bg-white border border-callisto rounded-xl divide-y divide-callisto/60">
        {units.map((u) => (
          <div
            key={u.id}
            className={`flex items-center gap-3 px-4 py-2.5 ${u.archived ? "opacity-60" : ""}`}
            data-testid={`territory-row-${u.name}`}
          >
            <MapPin size={14} className="text-callisto-muted shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-callisto-navy truncate">
                {u.name}
                {u.archived && (
                  <span className="ml-2 text-[10px] font-bold uppercase tracking-wider text-orange-700">Archived</span>
                )}
              </p>
              <p className="text-[11px] text-callisto-muted">{KIND_LABEL[u.kind] || u.kind}</p>
            </div>
            {u.archived ? (
              <Button
                variant="outline" size="sm" onClick={() => onRestore(u)}
                className="h-8 border-callisto text-callisto-navy text-[11px] font-semibold"
                data-testid={`territory-restore-${u.name}`}
              >
                <RotateCcw size={12} className="mr-1" /> Restore
              </Button>
            ) : (
              <div className="flex gap-2 shrink-0">
                <Button
                  variant="outline" size="sm" onClick={() => onRename(u)}
                  className="h-8 border-callisto text-callisto-navy text-[11px] font-semibold"
                  data-testid={`territory-rename-${u.name}`}
                >
                  <Pencil size={12} className="mr-1" /> Rename
                </Button>
                <Button
                  variant="outline" size="sm" onClick={() => onArchive(u)}
                  className="h-8 border-orange-200 text-orange-700 hover:bg-orange-50 text-[11px] font-semibold"
                  data-testid={`territory-archive-${u.name}`}
                >
                  <Archive size={12} className="mr-1" /> Archive
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
